/**
 * ActiveLivenessStep (Challenge-Based)
 * =====================================
 * Browser-based liveness detection using face landmark challenges.
 * Mobile-only: desktop users get a QR code to continue on phone.
 *
 * Challenge flow: Center face → Turn left → Turn right → Blink
 * 30-second timeout, 5 max retries.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Camera,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  MoveLeft,
  MoveRight,
  Scan,
} from 'lucide-react';
import { useFaceLandmarker } from './mediapipe/useFaceLandmarker';
import { useChallenge } from './mediapipe/useChallenge';
import { validateChallenge, calculateYaw, checkFacePosition } from './mediapipe/livenessUtils';
import { useReportStepError } from '../embed/EmbedContext';

const MAX_RETRIES = 5;

// Challenge icons
const CHALLENGE_ICONS = {
  center: Scan,
  turn_left: MoveLeft,
  turn_right: MoveRight,
};

export default function MediaPipeLivenessStep({ onComplete, onBack, client }) {
  const [phase, setPhase] = useState('intro'); // intro, loading, active, success, failed
  const [retryCount, setRetryCount] = useState(0);
  const [cameraError, setCameraError] = useState(null);
  useReportStepError('liveness', cameraError);
  const [sessionId, setSessionId] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const frameCountRef = useRef(0);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const { isLoading: modelLoading, loadProgress, error: modelError, detect } = useFaceLandmarker();
  const challenge = useChallenge();

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setCameraError(err.message || 'Camera access denied');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // Detection loop
  const runDetectionLoop = useCallback(() => {
    if (!videoRef.current || !detect) return;

    const loop = () => {
      if (challenge.status !== 'running') {
        rafRef.current = null;
        return;
      }

      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        const timestamp = performance.now();
        const result = detect(video, timestamp);
        frameCountRef.current++;

        if (result && result.faceLandmarks && result.faceLandmarks.length > 0) {
          const landmarks = result.faceLandmarks[0];
          const currentChallenge = challenge.currentChallenge;

          if (currentChallenge) {
            const yaw = calculateYaw(landmarks);

            // Check if challenge condition is met
            const conditionMet = validateChallenge(
              currentChallenge.id,
              landmarks,
              []
            );

            if (conditionMet) {
              challenge.onConditionMet();
            } else {
              challenge.onConditionNotMet();
            }

            // Debug info every 15 frames
            if (frameCountRef.current % 15 === 0) {
              const pos = checkFacePosition(landmarks);
              setDebugInfo(`Yaw: ${yaw.toFixed(1)}° | Size: ${pos.faceSize.toFixed(2)}`);
            }
          }
        } else {
          // No face detected
          if (frameCountRef.current % 30 === 0) {
            setDebugInfo(result ? 'No face landmarks detected' : 'Detection returned null');
          }
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
  }, [detect, challenge]);

  // Handle phase transitions
  useEffect(() => {
    if (phase === 'loading' && !modelLoading && !modelError) {
      startCamera().then(() => {
        setPhase('active');
        challenge.start();
      });
    }
  }, [phase, modelLoading, modelError, startCamera, challenge]);

  // Attach stream to video element when it becomes available (fixes race condition
  // where startCamera runs before the active phase video element is rendered)
  useEffect(() => {
    if (phase === 'active' && videoRef.current && streamRef.current) {
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }
    }
  }, [phase]);

  // Start detection loop when active
  useEffect(() => {
    if (phase === 'active' && challenge.status === 'running') {
      runDetectionLoop();
    }
  }, [phase, challenge.status, runDetectionLoop]);

  // Handle challenge completion
  useEffect(() => {
    if (challenge.status === 'passed') {
      setPhase('success');
      stopCamera();

      // Report to backend
      if (client) {
        client.completeLiveness(sessionId || 'mediapipe-local', 95.0, {
          provider: 'mediapipe',
          challengesCompleted: challenge.completedChallenges,
        })
          .then(() => {
            setTimeout(() => onComplete({ is_live: true, confidence: 95.0 }), 1500);
          })
          .catch(() => {
            setTimeout(() => onComplete({ is_live: true, confidence: 95.0 }), 1500);
          });
      } else {
        setTimeout(() => onComplete({ is_live: true, confidence: 95.0 }), 1500);
      }
    } else if (challenge.status === 'timeout') {
      setPhase('failed');
      stopCamera();
    }
  }, [challenge.status, challenge.completedChallenges, client, sessionId, onComplete, stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  // Handle start
  const handleStart = useCallback(async () => {
    setPhase('loading');
    try {
      const session = await client.createLivenessSession('mediapipe');
      setSessionId(session.session_id);
    } catch (err) {
      console.warn('Failed to create liveness session:', err);
    }
  }, [client]);

  // Handle retry
  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES) return;
    setRetryCount(prev => prev + 1);
    challenge.reset();
    setPhase('loading');
  }, [retryCount, challenge]);

  // === INTRO PHASE ===
  if (phase === 'intro') {
    return (
      <div style={styles.container}>
        <div style={styles.introContent}>
          <div style={styles.iconCircle}>
            <Scan size={32} style={{ color: 'var(--primary, #0D9488)' }} />
          </div>
          <h3 style={styles.title}>Liveness Check</h3>
          <p style={styles.subtitle}>
            We need to verify you're a real person. You'll be asked to:
          </p>
          <div style={styles.tipsList}>
            <div style={styles.tip}>
              <Scan size={16} style={{ color: 'var(--primary, #0D9488)' }} />
              <span>Center your face in the frame</span>
            </div>
            <div style={styles.tip}>
              <MoveLeft size={16} style={{ color: 'var(--primary, #0D9488)' }} />
              <span>Turn your head left and right</span>
            </div>
          </div>
          <p style={styles.tipNote}>
            Make sure you're in a well-lit area and remove sunglasses or hats.
          </p>
          <button style={styles.primaryButton} onClick={handleStart}>
            <Camera size={20} />
            Start Liveness Check
          </button>
          {onBack && (
            <button style={styles.backButton} onClick={onBack}>
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // === LOADING PHASE ===
  if (phase === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContent}>
          <Loader2 size={40} style={{ color: 'var(--primary, #0D9488)', animation: 'spin 1s linear infinite' }} />
          <h3 style={styles.title}>Preparing...</h3>
          <p style={styles.subtitle}>
            {modelLoading ? 'Loading AI model...' : 'Starting camera...'}
          </p>
          {modelLoading && (
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${loadProgress}%` }} />
            </div>
          )}
          {modelError && (
            <div style={styles.errorBox}>
              <AlertCircle size={16} color="#EF4444" />
              <span>{modelError}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // === SUCCESS PHASE ===
  if (phase === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.successContent}>
          <div style={styles.successIcon}>
            <CheckCircle size={48} color="#10B981" />
          </div>
          <h3 style={styles.title}>Liveness Verified!</h3>
          <p style={styles.subtitle}>Your identity has been confirmed.</p>
        </div>
      </div>
    );
  }

  // === FAILED PHASE ===
  if (phase === 'failed') {
    return (
      <div style={styles.container}>
        <div style={styles.failedContent}>
          <div style={styles.failedIcon}>
            <AlertCircle size={40} color="#EF4444" />
          </div>
          <h3 style={styles.title}>Verification Timed Out</h3>
          <p style={styles.subtitle}>
            We couldn't complete the liveness check in time.
          </p>
          {retryCount < MAX_RETRIES ? (
            <button style={styles.primaryButton} onClick={handleRetry}>
              <RefreshCw size={20} />
              Try Again ({MAX_RETRIES - retryCount} attempts left)
            </button>
          ) : (
            <p style={styles.errorText}>Maximum attempts reached. Please contact support.</p>
          )}
          {onBack && (
            <button style={styles.backButton} onClick={onBack}>
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // === ACTIVE PHASE - Camera + Challenges ===
  const ChallengeIcon = challenge.currentChallenge
    ? CHALLENGE_ICONS[challenge.currentChallenge.id] || Scan
    : Scan;

  return (
    <div style={styles.container}>
      {/* Camera feed */}
      <div style={styles.cameraContainer}>
        <video
          ref={videoRef}
          style={styles.video}
          playsInline
          muted
          autoPlay
        />
        {/* Face oval overlay */}
        <div style={styles.ovalOverlay}>
          <svg viewBox="0 0 300 400" style={styles.ovalSvg}>
            <defs>
              <mask id="ovalMask">
                <rect width="300" height="400" fill="white" />
                <ellipse cx="150" cy="200" rx="110" ry="150" fill="black" />
              </mask>
            </defs>
            <rect width="300" height="400" fill="rgba(0,0,0,0.5)" mask="url(#ovalMask)" />
            <ellipse
              cx="150" cy="200" rx="110" ry="150"
              fill="none"
              strokeWidth="3"
              strokeDasharray={challenge.holdProgress > 0 ? `${challenge.holdProgress * 7.5} 750` : 'none'}
              style={{ stroke: challenge.status === 'running' ? 'var(--primary, #0D9488)' : '#10B981' }}
            />
          </svg>
        </div>

        {/* Camera error */}
        {cameraError && (
          <div style={styles.cameraErrorOverlay}>
            <AlertCircle size={24} color="#EF4444" />
            <p>{cameraError}</p>
          </div>
        )}
      </div>

      {/* Challenge instruction */}
      <div style={styles.challengeBar}>
        {challenge.currentChallenge && (
          <>
            <div style={styles.challengeIconRow}>
              <ChallengeIcon size={26} style={{ color: 'var(--primary, #0D9488)' }} />
            </div>
            <p style={styles.challengeInstruction}>
              {challenge.currentChallenge.instruction}
            </p>
            {/* Hold progress indicator */}
            {challenge.holdProgress > 0 && challenge.holdProgress < 100 && (
              <div style={styles.holdBar}>
                <div style={{ ...styles.holdFill, width: `${challenge.holdProgress}%` }} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Debug info */}
      {debugInfo && (
        <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '0 0 8px', fontFamily: 'monospace' }}>
          {debugInfo}
        </p>
      )}

      {/* Progress dots */}
      <div style={styles.progressDots}>
        {challenge.challenges.map((c, i) => (
          <div
            key={c.id}
            style={{
              ...styles.dot,
              background: challenge.completedChallenges.includes(c.id)
                ? '#10B981'
                : i === challenge.currentIndex
                  ? 'var(--primary, #0D9488)'
                  : '#D1D5DB',
            }}
          />
        ))}
      </div>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

/* iOS 2026 Standards: 22px title, 17px body, 44px touch targets */
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    position: 'relative',
  },
  introContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '20px',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
  },
  successContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  failedContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconCircle: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: '#EEF2FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#D1FAE5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  failedIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: '#FEE2E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '0 0 8px',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '17px',
    color: '#6B7280',
    margin: '0 0 20px',
    lineHeight: '1.5',
  },
  tipsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '300px',
  },
  tip: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '17px',
    color: '#374151',
    textAlign: 'left',
  },
  tipNote: {
    fontSize: '15px',
    color: '#9CA3AF',
    margin: '0 0 20px',
    maxWidth: '300px',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px 28px',
    minHeight: '50px', /* iOS primary button */
    minWidth: '200px',
    background: 'var(--primary, #0D9488)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '12px',
    WebkitTapHighlightColor: 'transparent',
  },
  backButton: {
    padding: '12px 20px',
    minHeight: '44px', /* iOS touch target */
    background: 'transparent',
    color: '#6B7280',
    border: 'none',
    fontSize: '17px',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  progressBar: {
    width: '200px',
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'var(--primary, #0D9488)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 18px',
    background: '#FEF2F2',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#DC2626',
    marginTop: '12px',
  },
  errorText: {
    fontSize: '15px',
    color: '#EF4444',
    margin: '8px 0',
    lineHeight: '1.5',
  },
  // Camera view - mobile optimized for 2026 iOS
  cameraContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: 'min(300px, 85vw)',
    maxHeight: '45vh',
    aspectRatio: '3/4',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#000',
    marginBottom: '12px',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)', // Mirror
  },
  ovalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  ovalSvg: {
    width: '100%',
    height: '100%',
  },
  cameraErrorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    background: 'rgba(0,0,0,0.85)',
    color: '#FFFFFF',
    padding: '20px',
    textAlign: 'center',
    fontSize: '17px',
    lineHeight: '1.5',
  },
  challengeBar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  challengeIconRow: {
    width: '44px', /* iOS touch target */
    height: '44px',
    borderRadius: '50%',
    background: '#EEF2FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  challengeInstruction: {
    fontSize: '17px',
    fontWeight: '500',
    color: '#1F2937',
    margin: '0',
    textAlign: 'center',
    lineHeight: '1.4',
  },
  holdBar: {
    width: 'min(160px, 65vw)',
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
    marginTop: '6px',
  },
  holdFill: {
    height: '100%',
    background: 'var(--primary, #0D9488)',
    borderRadius: '3px',
    transition: 'width 0.1s linear',
  },
  progressDots: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    padding: '8px 0',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    transition: 'background 0.3s ease, transform 0.2s ease',
  },
};
